/**
 * Plan 13-02 — BulkApproveDialog
 *
 * Bulk-approve dialog: shows count of selected entries, optional remarks,
 * submits via `useBulkApproveMutation`, displays progress while running,
 * and shows per-id success/failure summary after resolution.
 *
 * T-13-14 DoS guard: enforces `ids.length <= 100` before POST.
 * Uses react-hook-form + Zod via useZodForm.
 */

import type { JSX } from 'react';
import { useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useBulkApproveMutation } from '@/hooks/useLogsheetCheckerQueries';
import { useZodForm } from '@/lib/forms/useZodForm';
import type { BulkApproveResult } from '@/types/logsheet-checker';

const MAX_BULK_IDS = 100;

const bulkApproveSchema = z.object({
  remarks: z.string().max(500).optional(),
});

type BulkApproveFormValues = z.input<typeof bulkApproveSchema>;

export interface BulkApproveDialogProps {
  readonly open: boolean;
  readonly entryIds: readonly number[];
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export function BulkApproveDialog({
  open,
  entryIds,
  onClose,
  onSuccess,
}: BulkApproveDialogProps): JSX.Element {
  const [result, setResult] = useState<BulkApproveResult | null>(null);

  const form = useZodForm({
    schema: bulkApproveSchema,
    defaultValues: { remarks: '' },
  });

  const mutation = useBulkApproveMutation();

  const cappedIds = entryIds.slice(0, MAX_BULK_IDS);
  const isCapped = entryIds.length > MAX_BULK_IDS;

  function handleClose(): void {
    form.reset();
    setResult(null);
    mutation.reset();
    onClose();
  }

  const onSubmit = form.handleSubmit((values: BulkApproveFormValues) => {
    const remarks = values.remarks?.trim();
    const body = {
      ids: [...cappedIds],
      ...(remarks !== undefined && remarks !== '' ? { remarks } : {}),
    };
    mutation.mutate(body, {
      onSuccess: (data) => {
        setResult(data);
        if (data.failedCount === 0) {
          onSuccess();
        }
      },
    });
  });

  const showResult = result !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent data-testid="bulk-approve-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Approve</DialogTitle>
          <DialogDescription>
            Approve{' '}
            <span data-testid="bulk-approve-dialog-count" className="font-semibold">
              {cappedIds.length}
            </span>{' '}
            selected {cappedIds.length === 1 ? 'entry' : 'entries'}.
            {isCapped && (
              <span className="ml-1 text-xs text-yellow-600">
                (capped at {MAX_BULK_IDS} — {entryIds.length - MAX_BULK_IDS} not submitted)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {!showResult ? (
          <form onSubmit={onSubmit} noValidate>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label htmlFor="bulk-approve-remarks">Remarks (optional)</Label>
                <Textarea
                  id="bulk-approve-remarks"
                  placeholder="Optional remarks applied to all selected entries…"
                  rows={3}
                  data-testid="bulk-approve-dialog-remarks"
                  {...form.register('remarks')}
                />
                {form.formState.errors.remarks !== undefined && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.remarks.message}
                  </p>
                )}
              </div>
            </div>
            {mutation.isPending && (
              <div
                className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted"
                aria-label="Approving…"
              >
                <div className="h-full w-1/2 animate-pulse rounded-full bg-primary" />
              </div>
            )}
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={mutation.isPending}
                data-testid="bulk-approve-dialog-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={mutation.isPending || cappedIds.length === 0}
                data-testid="bulk-approve-dialog-submit"
              >
                {mutation.isPending
                  ? 'Approving…'
                  : `Approve ${String(cappedIds.length)} ${cappedIds.length === 1 ? 'entry' : 'entries'}`}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3 py-2">
            <p className="text-sm font-medium" data-testid="bulk-approve-dialog-result-summary">
              {result.succeededCount} approved, {result.failedCount} failed
            </p>
            {result.failedCount > 0 && (
              <ul className="space-y-1">
                {result.results
                  .filter((r) => !r.success)
                  .map((r) => (
                    <li
                      key={r.id}
                      className="text-xs text-destructive"
                      data-testid={`bulk-approve-dialog-result-row-${String(r.id)}`}
                    >
                      Entry #{r.id}: {r.error ?? 'Unknown error'}
                    </li>
                  ))}
              </ul>
            )}
            <DialogFooter>
              <Button type="button" onClick={handleClose} data-testid="bulk-approve-dialog-cancel">
                Close
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
